/**
 * WooCommerce notice handler.
 *
 * For each .woocommerce-message / .woocommerce-info / .woocommerce-error
 * inside .woocommerce-notices-wrapper — rendered with the page, or added
 * later by WooCommerce's scripts (the cart's "Shipping costs updated.",
 * "Cart updated.", coupon results) — this:
 *   - appends a close (X) button
 *   - starts an auto-dismiss timer (default 5s) UNLESS the body has
 *     class .tx-no-notice-auto-dismiss (controlled by the WooCommerce
 *     tab in Theme Options)
 *   - pauses the timer while the notice is hovered or holds focus
 *   - dismisses (animate out + remove from DOM) when the X is clicked
 *     or the timer elapses
 *
 * Dismissal is purely visual — WC's session-stored notices clear on
 * the next request as usual.
 */

// Only handle alerts inside the floating wrapper. Inline notices
// (e.g. checkout's "Have a coupon?" disclosure) are UI controls, not
// dismissible alerts — they should not get an X or auto-close timer.
const SELECTOR = '.woocommerce-notices-wrapper > .woocommerce-message, '
    + '.woocommerce-notices-wrapper > .woocommerce-info, '
    + '.woocommerce-notices-wrapper > .woocommerce-error';
const DURATION = 5000;

const initNotice = (notice, autoDismiss) => {
    if (notice.dataset.txNotice) return;
    notice.dataset.txNotice = '1';

    // --- Close button ---
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tx-notice__close';
    closeBtn.setAttribute('aria-label', 'Dismiss notification');
    closeBtn.innerHTML = '<span aria-hidden="true">×</span>';
    notice.appendChild(closeBtn);

    let dismissed = false;
    let timerId = null;

    const dismiss = () => {
        if (dismissed) return;
        dismissed = true;
        window.clearTimeout(timerId);
        notice.classList.add('is-dismissing');

        const cleanup = () => notice.remove();
        notice.addEventListener('transitionend', cleanup, { once: true });
        // safety net in case transitionend doesn't fire (display:none, etc.)
        window.setTimeout(cleanup, 400);
    };

    closeBtn.addEventListener('click', dismiss);

    // When auto-dismiss is OFF, the X is the only way to close — no timer,
    // no pausing needed.
    if (!autoDismiss) return;

    let startedAt = 0;
    let remaining = DURATION;
    let hovered = false;
    let focused = false;

    const start = () => {
        if (dismissed || timerId !== null) return;
        startedAt = Date.now();
        timerId = window.setTimeout(dismiss, remaining);
    };

    const pause = () => {
        if (timerId === null) return;
        window.clearTimeout(timerId);
        timerId = null;
        remaining = Math.max(0, remaining - (Date.now() - startedAt));
    };

    // Held while either the pointer is over it or focus is inside it —
    // WooCommerce focuses error notices itself, so they wait to be read.
    const sync = () => (hovered || focused ? pause() : start());

    notice.addEventListener('mouseenter', () => { hovered = true; sync(); });
    notice.addEventListener('mouseleave', () => { hovered = false; sync(); });
    notice.addEventListener('focusin', () => { focused = true; sync(); });
    notice.addEventListener('focusout', (event) => {
        if (notice.contains(event.relatedTarget)) return;
        focused = false;
        sync();
    });

    focused = notice.contains(document.activeElement);
    sync();
};

const initWithin = (root, autoDismiss) => {
    if (root.matches && root.matches(SELECTOR)) initNotice(root, autoDismiss);
    root.querySelectorAll(SELECTOR).forEach((n) => initNotice(n, autoDismiss));
};

const init = () => {
    const autoDismiss = !document.body.classList.contains('tx-no-notice-auto-dismiss');
    initWithin(document, autoDismiss);

    // Notices WooCommerce adds after an AJAX update.
    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) initWithin(node, autoDismiss);
            });
        });
    }).observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
